/**
 * The editor for one draft: top bar; left, the layers tree and the asset library; centre, the toolbar and canvas;
 * right, the inspector or the Data panel; bottom, Problems and the animation timeline.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ApiError, api, type ClientConfig, type Draft } from './api.ts';
import { t } from './i18n.ts';
import type { Scene } from '@prism-graphics/scene-schema';
import type { StaticFrame } from '@prism-graphics/runtime/static';
import { Canvas, type Frames } from './canvas/Canvas.tsx';
import { LayoutTabs } from './canvas/LayoutTabs.tsx';
import { computeProblems, type FontInfo } from './model/problems.ts';
import { AssetsPanel } from './panels/AssetsPanel.tsx';
import { BrandPanel } from './panels/BrandPanel.tsx';
import { BottomPanel } from './panels/BottomPanel.tsx';
import { ConflictDialog, ShortcutsDialog } from './panels/Dialogs.tsx';
import { ChangeFormatDialog } from './ChangeFormatDialog.tsx';
import { DataPanel } from './panels/DataPanel.tsx';
import { Inspector } from './panels/Inspector.tsx';
import { LayersPanel } from './panels/LayersPanel.tsx';
import { Toolbar } from './panels/Toolbar.tsx';
import { TopBar } from './panels/TopBar.tsx';
import { PreviewPanel } from './preview/PreviewPanel.tsx';
import { SaveFlowDialogs, useSaveFlow } from './prism/SaveFlow.tsx';
import { prism, usePrism } from './prism/session.ts';
import { handleKeydown, isEditable, modalOpen } from './shortcuts.ts';
import { copyToClipboard, cutToClipboard, pasteFromText } from './state/actions.ts';
import { assetCache } from './state/assets.ts';
import { brandCache } from './state/brand.ts';
import { EditorStore, sceneView, useEditor } from './state/store.ts';
import { onTablistKeyDown, tabProps } from './ui/controls.tsx';

export function EditorPage({ draftId, config, onHome, embedded = false }: { draftId: string; config: ClientConfig | undefined; onHome: () => void; embedded?: boolean }) {
  const [store, setStore] = useState<EditorStore>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let alive = true;
    let s: EditorStore | undefined;
    (async () => {
      try {
        const [draft] = await Promise.all([api.getDraft(draftId), assetCache.libraryLoaded ? Promise.resolve() : assetCache.refreshLibrary()]);
        if (!alive) return;
        s = new EditorStore(draft as Draft);
        (window as unknown as { __pgEditor?: EditorStore }).__pgEditor = s;
        setStore(s);
      } catch (e) {
        if (alive) setError(e instanceof ApiError && e.status === 404 ? t('editor.notFound') : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
      if (s) {
        // The final save (it waits for one in flight, then saves what is left), then no more autosaves.
        void s.saveNow();
        s.dispose();
      }
    };
  }, [draftId]);

  if (error)
    return (
      <div className="center-message" role="alert">
        <p>{error}</p>
        <button type="button" className="btn" onClick={onHome}>
          {t('editor.backToDrafts')}
        </button>
      </div>
    );
  if (!store) return <div className="center-message">{t('editor.loading')}</div>;
  return <Editor store={store} config={config} onHome={onHome} embedded={embedded} />;
}

function Editor({ store, config, onHome, embedded }: { store: EditorStore; config: ClientConfig | undefined; onHome: () => void; embedded: boolean }) {
  const flow = useSaveFlow(store);
  const scene = useEditor(store, sceneView);
  const testData = useEditor(store, (s) => s.testData);
  const rightTab = useEditor(store, (s) => s.ui.rightTab);
  const graphicsSafe = useEditor(store, (s) => s.ui.graphicsSafe);
  const timelineOpen = useEditor(store, (s) => s.ui.bottomTab === 'timeline');
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const [frames, setFrames] = useState<Frames>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewOpenRef = useRef(false);
  previewOpenRef.current = previewOpen;
  const onFrames = useCallback((f: Frames) => setFrames(f), []);
  const languages = config?.glyphLanguages ?? ['fi', 'sv', 'en'];
  useEffect(() => {
    void brandCache.load();
  }, []);

  // The PRISM session expired: read-only and no autosave until a fresh token arrives (then pending changes save).
  // An autosave refused with 401 expires the session.
  const sessionExpired = usePrism((s) => s.status === 'expired');
  useEffect(() => store.setReadOnly(sessionExpired), [store, sessionExpired]);
  useEffect(() => {
    store.onUnauthorized = () => prism.expire();
    return () => {
      store.onUnauthorized = undefined;
    };
  }, [store]);

  // Keyboard shortcuts and the clipboard.
  useEffect(() => {
    // Clipboard: the document's copy/cut/paste events (no permission prompt). Where a browser does not turn the
    // keystroke into an event (some automation and embedded contexts), a short fallback uses the async API.
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const cancelFallback = () => {
      if (fallback) clearTimeout(fallback);
      fallback = undefined;
    };
    // A modal dialog (the preview, the save flow, a prompt) is open: no editor shortcuts or clipboard actions.
    const blocked = () => previewOpenRef.current || modalOpen();
    const onKey = (e: KeyboardEvent) => {
      if (blocked()) return;
      const modKey = navigator.platform.includes('Mac') ? e.metaKey : e.ctrlKey;
      if (modKey && !e.shiftKey && !e.altKey && !isEditable(e.target) && ['c', 'x', 'v'].includes(e.key.toLowerCase())) {
        const k = e.key.toLowerCase();
        cancelFallback();
        fallback = setTimeout(() => {
          fallback = undefined;
          if (blocked()) return;
          if (k === 'c') copyToClipboard(store);
          else if (k === 'x') cutToClipboard(store);
          else
            void (navigator.clipboard?.readText() ?? Promise.reject(new Error('no clipboard'))).then(
              (text) => pasteFromText(store, text) || pasteFromText(store, undefined),
              () => pasteFromText(store, undefined),
            );
        }, 80);
        return;
      }
      handleKeydown(store, e);
    };
    const onCopy = (e: ClipboardEvent) => {
      cancelFallback();
      if (blocked()) return;
      if (!isEditable(e.target) && store.state.selection.length) copyToClipboard(store, e);
    };
    const onCut = (e: ClipboardEvent) => {
      cancelFallback();
      if (blocked()) return;
      if (!isEditable(e.target) && store.state.selection.length) cutToClipboard(store, e);
    };
    const onPaste = (e: ClipboardEvent) => {
      cancelFallback();
      if (blocked()) return;
      if (isEditable(e.target)) return;
      if (pasteFromText(store, e.clipboardData?.getData('text/plain'))) e.preventDefault();
    };
    // Save before leaving; warn if that is not possible.
    const onHide = () => {
      if (store.dirty) void store.saveNow({ keepalive: true });
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (store.dirty) {
        void store.saveNow({ keepalive: true });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    document.addEventListener('paste', onPaste);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      cancelFallback();
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCut);
      document.removeEventListener('paste', onPaste);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [store]);

  const fonts = useMemo(() => {
    const m = new Map<string, FontInfo>();
    for (const a of assetCache.library) if (a.kind === 'font') m.set(a.hash, { family: a.meta.family ?? a.originalName, codePoints: a.meta.codePoints, coverage: a.meta.coverage } as FontInfo);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCache.version]);

  const media = useMemo(() => new Map(assetCache.library.map((a) => [a.hash, { size: a.size, mime: a.mime, ...(a.meta.alpha !== undefined ? { alpha: a.meta.alpha } : {}) }])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetCache.version]);

  const problems = useMemo(
    () =>
      computeProblems({
        scene,
        test: frames.test,
        defaults: frames.defaults,
        fonts,
        library: assetCache.libraryLoaded ? new Set(assetCache.library.map((a) => a.hash)) : undefined,
        languages,
        safeMargin: graphicsSafe,
        testData,
        media,
        ...(config?.limits.packageBytes ? { packageLimit: config.limits.packageBytes } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scene, frames, fonts, media, languages.join(','), graphicsSafe, testData, config?.limits.packageBytes],
  );

  // Problems of a layout that is not on the canvas (the layout tabs' counts).
  const countProblems = useCallback(
    (layoutScene: Scene, frame: StaticFrame) =>
      computeProblems({
        scene: layoutScene,
        test: frame,
        fonts,
        library: assetCache.libraryLoaded ? new Set(assetCache.library.map((a) => a.hash)) : undefined,
        languages,
        safeMargin: graphicsSafe,
        testData,
        media,
        ...(config?.limits.packageBytes ? { packageLimit: config.limits.packageBytes } : {}),
      }).filter((p) => p.severity !== 'info').length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fonts, media, languages.join(','), graphicsSafe, testData, config?.limits.packageBytes],
  );

  return (
    <div className={`editor${timelineOpen ? ' timeline-open' : ''}`} data-testid="editor">
      <TopBar store={store} onHome={onHome} onPreview={() => setPreviewOpen(true)} flow={flow} embedded={embedded} />
      <aside className="left" aria-label={t('left.label')}>
        <section className="panel layers-panel" aria-labelledby="layers-h">
          <h2 className="panel-title" id="layers-h">
            {t('layers.title')}
          </h2>
          <LayersPanel store={store} />
        </section>
        <section className="panel assets-panel" aria-labelledby="assets-h">
          <h2 className="panel-title" id="assets-h">
            {t('assets.title')}
          </h2>
          <AssetsPanel store={store} languages={languages} />
        </section>
      </aside>
      <main className="centre" aria-label={t('canvas.region')}>
        <Toolbar store={store} />
        <LayoutTabs store={store} activeCount={problems.filter((p) => p.severity !== 'info').length} countProblems={countProblems} />
        <Canvas store={store} problems={problems} onFrames={onFrames} />
      </main>
      <aside className="right" aria-label={t('right.label')}>
        <div className="tabs" role="tablist" aria-label={t('right.label')} onKeyDown={onTablistKeyDown}>
          <button type="button" {...tabProps(rightTab === 'design', 'right-panel')} id="right-tab-design" className={rightTab === 'design' ? 'active' : ''} onClick={() => store.setUi({ rightTab: 'design' })} data-testid="tab-design">
            {t('right.design')}
          </button>
          <button type="button" {...tabProps(rightTab === 'data', 'right-panel')} id="right-tab-data" className={rightTab === 'data' ? 'active' : ''} onClick={() => store.setUi({ rightTab: 'data' })} data-testid="tab-data">
            {t('right.data')} {scene.fields.length > 0 && <span className="count">{scene.fields.length}</span>}
          </button>
          <button type="button" {...tabProps(rightTab === 'brand', 'right-panel')} id="right-tab-brand" className={rightTab === 'brand' ? 'active' : ''} onClick={() => store.setUi({ rightTab: 'brand' })} data-testid="tab-brand">
            {t('right.brand')}
          </button>
        </div>
        <div className="right-body" role="tabpanel" id="right-panel" aria-labelledby={`right-tab-${rightTab}`}>
          {rightTab === 'design' ? <Inspector store={store} frame={frames.test} /> : rightTab === 'data' ? <DataPanel store={store} /> : <BrandPanel store={store} />}
        </div>
      </aside>
      <BottomPanel store={store} problems={problems} />
      <ShortcutsDialog store={store} />
      <ChangeFormatDialog store={store} />
      <ConflictDialog store={store} />
      <SaveFlowDialogs flow={flow} />
      {previewOpen && <PreviewPanel store={store} limitBytes={config?.limits.packageBytes} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}
